import React, { useState, useEffect } from "react";
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  TrendingUp,
  Users,
  BarChart3,
  Loader2,
  CheckCircle2,
  AlertCircle,
  CreditCard,
  X,
  Gift,
  Share2,
  Copy,
  Link,
  Building2,
  UserPlus,
  Activity,
  DollarSign,
  ArrowRight,
  ChevronRight,
  User,
  LogOut,
  Mail,
  ChevronDown,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

// ==================== HEALTH TIER CONFIG ====================
const TIERS = {
  premium:        { label: 'Premium',          range: '80–100%', color: '#10b981' },
  good:           { label: 'Good Standing',    range: '50–79%',  color: '#3b82f6' },
  needsAttention: { label: 'Needs Attention',  range: '30–49%',  color: '#f59e0b' },
  atRisk:         { label: 'At Risk',          range: '<30%',    color: '#f43f5e' },
};

// ==================== MAIN COMPONENT ====================
const AdminPanel = ({ onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [clientMetrics, setClientMetrics] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ title: '', companies: [], color: '#6366f1' });
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [referralCopied, setReferralCopied] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [loadingReferralStats, setLoadingReferralStats] = useState(false);
  const [allReferrals, setAllReferrals] = useState([]);
  const orgName = localStorage.getItem('org_name') || localStorage.getItem('full_name') || 'Admin';
  const userFullName = localStorage.getItem('full_name') || '';
  const userEmail = localStorage.getItem('admin_email') || '';
  const userInitials = userFullName ? userFullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : userEmail[0]?.toUpperCase() || 'U';
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // ==================== METRICS CALCULATION ====================
  const calculateReferralMetrics = (referrals) => {
    if (!referrals || referrals.length === 0) return null;

    const organizations = referrals.map(ref => ({
      id: ref.id,
      name: ref.referredOrganization?.name || 'Unknown Organization',
      companyName: ref.referredOrganization?.name || 'Unknown Organization',
      totalInvoices: ref.totalInvoices || 0,
      totalAmount: parseFloat(ref.totalInvoiceAmount || 0),
      paidAmount: parseFloat(ref.totalPaidAmount || 0),
      outstandingAmount: parseFloat(ref.totalOutstandingAmount || 0),
      status: ref.status,
      createdAt: ref.createdAt,
    }));

    const organizationMetrics = organizations.map(org => {
      const paymentScore = org.totalAmount > 0
        ? Math.round((org.paidAmount / org.totalAmount) * 100)
        : 0;
      let engagementScore = 0;
      if (org.totalInvoices >= 10) engagementScore = 95;
      else if (org.totalInvoices >= 5) engagementScore = 70;
      else if (org.totalInvoices >= 2) engagementScore = 40;
      else engagementScore = 20;
      const healthScore = Math.round((paymentScore * 0.6) + (engagementScore * 0.4));
      return {
        ...org, healthScore, paymentScore, engagementScore,
        paidInvoices: Math.round((paymentScore / 100) * org.totalInvoices),
        overdueInvoices: 0,
        recentInvoices: org.totalInvoices,
        totalRevenue: org.totalAmount,
      };
    });

    const premium        = organizationMetrics.filter(o => o.healthScore >= 80);
    const good           = organizationMetrics.filter(o => o.healthScore >= 50 && o.healthScore < 80);
    const needsAttention = organizationMetrics.filter(o => o.healthScore >= 30 && o.healthScore < 50);
    const atRisk         = organizationMetrics.filter(o => o.healthScore < 30);

    const alwaysOnTime   = organizationMetrics.filter(o => o.paymentScore >= 90);
    const usuallyOnTime  = organizationMetrics.filter(o => o.paymentScore >= 60 && o.paymentScore < 90);
    const sometimesLate  = organizationMetrics.filter(o => o.paymentScore >= 30 && o.paymentScore < 60);
    const oftenLate      = organizationMetrics.filter(o => o.paymentScore < 30);

    const highlyActive   = organizationMetrics.filter(o => o.totalInvoices >= 10);
    const regularActive  = organizationMetrics.filter(o => o.totalInvoices >= 5 && o.totalInvoices < 10);
    const occasional     = organizationMetrics.filter(o => o.totalInvoices >= 2 && o.totalInvoices < 5);
    const inactive       = organizationMetrics.filter(o => o.totalInvoices < 2);

    const sorted = [...organizationMetrics].sort((a, b) => b.healthScore - a.healthScore);

    const avgHealth         = organizationMetrics.length > 0 ? Math.round(organizationMetrics.reduce((s, o) => s + o.healthScore, 0) / organizationMetrics.length) : 0;
    const avgPaymentScore   = organizationMetrics.length > 0 ? Math.round(organizationMetrics.reduce((s, o) => s + o.paymentScore, 0) / organizationMetrics.length) : 0;
    const avgInvoicesPerOrg = organizationMetrics.length > 0 ? (organizationMetrics.reduce((s, o) => s + o.totalInvoices, 0) / organizationMetrics.length).toFixed(1) : 0;
    const totalRevenue      = organizationMetrics.reduce((s, o) => s + o.totalRevenue, 0);

    const pct = (arr) => organizations.length > 0 ? (arr.length / organizations.length) * 100 : 0;

    return {
      totalClients: organizations.length,
      healthDistribution: {
        premium:        { count: premium.length,        percentage: pct(premium),        companies: premium },
        good:           { count: good.length,           percentage: pct(good),           companies: good },
        needsAttention: { count: needsAttention.length, percentage: pct(needsAttention), companies: needsAttention },
        atRisk:         { count: atRisk.length,         percentage: pct(atRisk),         companies: atRisk },
      },
      paymentBehavior: {
        alwaysOnTime:  { count: alwaysOnTime.length,  percentage: pct(alwaysOnTime),  companies: alwaysOnTime },
        usuallyOnTime: { count: usuallyOnTime.length, percentage: pct(usuallyOnTime), companies: usuallyOnTime },
        sometimesLate: { count: sometimesLate.length, percentage: pct(sometimesLate), companies: sometimesLate },
        oftenLate:     { count: oftenLate.length,     percentage: pct(oftenLate),     companies: oftenLate },
      },
      engagement: {
        highlyActive:  { count: highlyActive.length,  percentage: pct(highlyActive),  companies: highlyActive },
        regularActive: { count: regularActive.length, percentage: pct(regularActive), companies: regularActive },
        occasional:    { count: occasional.length,    percentage: pct(occasional),    companies: occasional },
        inactive:      { count: inactive.length,      percentage: pct(inactive),      companies: inactive },
      },
      topPerformers:      sorted.slice(0, 3),
      bottomPerformers:   sorted.slice(-3).reverse(),
      avgHealth, avgPaymentScore,
      avgOrdersPerClient: avgInvoicesPerOrg,
      totalRevenue: totalRevenue.toFixed(2),
    };
  };

  // ==================== DATA FETCHING ====================
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token  = localStorage.getItem('admin_token') || localStorage.getItem('supabase_access_token');
        const userId = localStorage.getItem('user_id');
        if (!token || !userId) { setLoading(false); return; }

        const response = await fetch(`${API_BASE_URL}/referrals?user_id=${userId}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          setAllReferrals(data || []);
          setClientMetrics(calculateReferralMetrics(data || []));
        } else {
          setAllReferrals([]); setClientMetrics(null);
        }
      } catch (e) {
        setAllReferrals([]); setClientMetrics(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      setLoadingReferralStats(true);
      try {
        const token  = localStorage.getItem('admin_token') || localStorage.getItem('supabase_access_token');
        const userId = localStorage.getItem('user_id');
        if (!token || !userId) return;
        const response = await fetch(`${API_BASE_URL}/referrals/my-stats?user_id=${userId}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) setReferralStats(await response.json());
      } catch (e) {
        // silent
      } finally {
        setLoadingReferralStats(false);
      }
    };
    fetchStats();
  }, []);

  const openReferralModal = async () => {
    setShowReferralModal(true);
    setReferralCopied(false);
    const userId = localStorage.getItem('user_id');
    setReferralLink(userId ? `https://manage.evolvv.app/signup?ref=${userId}` : 'https://manage.evolvv.app/signup');
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    });
  };

  const showCompanyList = (title, companies, color) => {
    setModalData({ title, companies, color: color || '#6366f1' });
    setShowModal(true);
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
          </div>
          <p className="text-sm font-medium text-slate-700">Loading your dashboard…</p>
          <p className="text-xs text-slate-400 mt-1">Fetching organization data</p>
        </div>
      </div>
    );
  }

  // ==================== EMPTY STATE ====================
  const EMPTY = {
    totalClients: 0,
    healthDistribution: { premium: { count: 0, percentage: 0, companies: [] }, good: { count: 0, percentage: 0, companies: [] }, needsAttention: { count: 0, percentage: 0, companies: [] }, atRisk: { count: 0, percentage: 0, companies: [] } },
    paymentBehavior:    { alwaysOnTime: { count: 0, percentage: 0, companies: [] }, usuallyOnTime: { count: 0, percentage: 0, companies: [] }, sometimesLate: { count: 0, percentage: 0, companies: [] }, oftenLate: { count: 0, percentage: 0, companies: [] } },
    engagement:         { highlyActive: { count: 0, percentage: 0, companies: [] }, regularActive: { count: 0, percentage: 0, companies: [] }, occasional: { count: 0, percentage: 0, companies: [] }, inactive: { count: 0, percentage: 0, companies: [] } },
    topPerformers: [], bottomPerformers: [],
    avgHealth: 0, avgPaymentScore: 0, avgOrdersPerClient: 0, totalRevenue: '0.00',
  };

  const { totalClients, healthDistribution, paymentBehavior, engagement, topPerformers, bottomPerformers, avgHealth, avgPaymentScore, avgOrdersPerClient, totalRevenue } = clientMetrics || EMPTY;

  const chartData = [
    { name: 'Premium',        value: healthDistribution.premium.count,        color: TIERS.premium.color,        companies: healthDistribution.premium.companies,        title: 'Premium Organizations' },
    { name: 'Good',           value: healthDistribution.good.count,           color: TIERS.good.color,           companies: healthDistribution.good.companies,           title: 'Good Standing' },
    { name: 'Needs Attention',value: healthDistribution.needsAttention.count, color: TIERS.needsAttention.color, companies: healthDistribution.needsAttention.companies, title: 'Needs Attention' },
    { name: 'At Risk',        value: healthDistribution.atRisk.count,         color: TIERS.atRisk.color,         companies: healthDistribution.atRisk.companies,         title: 'At Risk' },
  ];

  const formatRevenue = (val) => {
    const n = parseFloat(val);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Top Navigation ── */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="./images/Evolvv-main.png" alt="Evolvv" className="h-6 object-contain" />
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-800">{orgName}</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                Insights
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={openReferralModal}
              size="sm"
              className="text-white text-xs h-8 px-3 gap-1.5 rounded-lg"
              style={{ background: '#2563eb' }}
            >
              <UserPlus className="h-3.5 w-3.5" />
              Invite Organizations
            </Button>

            {/* Profile dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(v => !v)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: '#2563eb' }}>
                  {userInitials}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-xs font-semibold text-slate-800 leading-tight max-w-[120px] truncate">{userFullName || userEmail}</div>
                  <div className="text-[10px] text-slate-400 leading-tight">{orgName}</div>
                </div>
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>

              {showProfileMenu && (
                <>
                  {/* Backdrop */}
                  <div className="fixed inset-0 z-30" onClick={() => setShowProfileMenu(false)} />
                  {/* Menu */}
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-100 z-40 overflow-hidden">
                    {/* Profile header */}
                    <div className="px-4 py-4 border-b border-slate-100" style={{ background: 'linear-gradient(135deg, #eff6ff, #ffffff)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: '#2563eb' }}>
                          {userInitials}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{userFullName || 'User'}</div>
                          <div className="text-xs text-slate-500 truncate">{userEmail}</div>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="px-4 py-3 space-y-2">
                      <div className="flex items-center gap-2.5 text-xs text-slate-600">
                        <Building2 className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#2563eb' }} />
                        <span className="truncate">{orgName}</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-xs text-slate-600">
                        <Mail className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#2563eb' }} />
                        <span className="truncate">{userEmail}</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-xs text-slate-600">
                        <User className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#2563eb' }} />
                        <span>Space Operator</span>
                      </div>
                    </div>

                    {/* Sign out */}
                    {onLogout && (
                      <div className="px-3 py-2 border-t border-slate-100">
                        <button
                          onClick={() => { setShowProfileMenu(false); onLogout(); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <LogOut className="h-3.5 w-3.5" />
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ── Page Content ── */}
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Page heading */}
        <div className="mb-8">
          <h1 className="text-xl font-bold text-slate-900">Overview</h1>
          <p className="text-slate-400 text-sm mt-0.5">Track performance across your member organizations</p>
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Organizations"
            value={totalClients}
            sub={referralStats ? `${referralStats.totalActive || 0} active` : 'Total members'}
            icon={Building2}
            iconBg="bg-violet-50"
            iconColor="text-violet-600"
          />
          <StatCard
            label="Avg Health Score"
            value={`${avgHealth}%`}
            sub="Overall portfolio health"
            icon={Activity}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
          />
          <StatCard
            label="Payment Score"
            value={`${avgPaymentScore}%`}
            sub="Avg payment reliability"
            icon={CheckCircle2}
            iconBg="bg-sky-50"
            iconColor="text-sky-600"
          />
          <StatCard
            label="Total Revenue"
            value={formatRevenue(totalRevenue)}
            sub="Billed across all orgs"
            icon={DollarSign}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
          />
        </div>

        {/* ── Main Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

          {/* Health Distribution — 2 cols */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-50">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Health Distribution</h2>
                <p className="text-xs text-slate-400 mt-0.5">Click any bar to view organizations in that tier</p>
              </div>
              <TrendingUp className="h-4 w-4 text-slate-300 mt-0.5" />
            </div>

            <div className="px-6 pt-4 pb-2">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barCategoryGap="40%"
                  onClick={(d) => {
                    if (d?.activePayload?.[0]) {
                      const p = d.activePayload[0].payload;
                      showCompanyList(p.title, p.companies, p.color);
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: '#f8fafc' }}
                    contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                    formatter={(value) => [`${value} organization${value !== 1 ? 's' : ''}`, '']}
                    labelStyle={{ fontWeight: 600, color: '#0f172a' }}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} cursor="pointer"
                    onClick={(d) => showCompanyList(d.title, d.companies, d.color)}
                  >
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="px-6 pb-6 space-y-3 border-t border-slate-50 pt-4">
              {Object.entries(TIERS).map(([key, tier]) => (
                <MetricBar
                  key={key}
                  label={`${tier.label} (${tier.range})`}
                  count={healthDistribution[key].count}
                  percentage={healthDistribution[key].percentage}
                  color={tier.color}
                  onClick={() => showCompanyList(`${tier.label} Organizations`, healthDistribution[key].companies, tier.color)}
                />
              ))}
            </div>
          </div>

          {/* Performers — 1 col */}
          <div className="space-y-5">
            <SectionCard
              title="Top Performers"
              icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            >
              {topPerformers.length > 0 ? topPerformers.map((org, i) => (
                <PerformerItem
                  key={i}
                  rank={i + 1}
                  name={org.name || org.companyName}
                  score={org.healthScore}
                  metric={`${org.totalInvoices} invoice${org.totalInvoices !== 1 ? 's' : ''}`}
                  color={TIERS.premium.color}
                />
              )) : (
                <EmptyNote text="No organizations yet. Invite some to get started!" />
              )}
            </SectionCard>

            <SectionCard
              title="Needs Focus"
              icon={<AlertCircle className="h-4 w-4 text-rose-400" />}
            >
              {bottomPerformers.length > 0 ? bottomPerformers.map((org, i) => (
                <PerformerItem
                  key={i}
                  rank={i + 1}
                  name={org.name || org.companyName}
                  score={org.healthScore}
                  metric={`${org.totalInvoices} invoice${org.totalInvoices !== 1 ? 's' : ''}`}
                  color={org.healthScore < 30 ? TIERS.atRisk.color : TIERS.needsAttention.color}
                />
              )) : (
                <EmptyNote text="All organizations are in good standing." />
              )}
            </SectionCard>
          </div>
        </div>

        {/* ── Payment Behavior & Engagement ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard title="Payment Behavior" icon={<CreditCard className="h-4 w-4 text-slate-400" />} padded>
            <div className="space-y-3">
              <MetricBar label="Always On Time (90%+)"    count={paymentBehavior.alwaysOnTime.count}  percentage={paymentBehavior.alwaysOnTime.percentage}  color={TIERS.premium.color}        onClick={() => showCompanyList('Always On Time',  paymentBehavior.alwaysOnTime.companies,  TIERS.premium.color)} />
              <MetricBar label="Usually On Time (60–89%)" count={paymentBehavior.usuallyOnTime.count} percentage={paymentBehavior.usuallyOnTime.percentage} color={TIERS.good.color}           onClick={() => showCompanyList('Usually On Time', paymentBehavior.usuallyOnTime.companies, TIERS.good.color)} />
              <MetricBar label="Sometimes Late (30–59%)"  count={paymentBehavior.sometimesLate.count} percentage={paymentBehavior.sometimesLate.percentage} color={TIERS.needsAttention.color} onClick={() => showCompanyList('Sometimes Late',  paymentBehavior.sometimesLate.companies, TIERS.needsAttention.color)} />
              <MetricBar label="Often Late (<30%)"         count={paymentBehavior.oftenLate.count}     percentage={paymentBehavior.oftenLate.percentage}     color={TIERS.atRisk.color}         onClick={() => showCompanyList('Often Late',      paymentBehavior.oftenLate.companies,     TIERS.atRisk.color)} />
            </div>
          </SectionCard>

          <SectionCard title="Engagement Levels" icon={<BarChart3 className="h-4 w-4 text-slate-400" />} padded>
            <div className="space-y-3">
              <MetricBar label="Highly Active (10+ invoices)"    count={engagement.highlyActive.count}  percentage={engagement.highlyActive.percentage}  color={TIERS.premium.color}        onClick={() => showCompanyList('Highly Active',    engagement.highlyActive.companies,  TIERS.premium.color)} />
              <MetricBar label="Regularly Active (5–9 invoices)" count={engagement.regularActive.count} percentage={engagement.regularActive.percentage} color={TIERS.good.color}           onClick={() => showCompanyList('Regularly Active', engagement.regularActive.companies, TIERS.good.color)} />
              <MetricBar label="Occasional (2–4 invoices)"       count={engagement.occasional.count}    percentage={engagement.occasional.percentage}    color={TIERS.needsAttention.color} onClick={() => showCompanyList('Occasional',        engagement.occasional.companies,    TIERS.needsAttention.color)} />
              <MetricBar label="Inactive (<2 invoices)"          count={engagement.inactive.count}      percentage={engagement.inactive.percentage}      color={TIERS.atRisk.color}         onClick={() => showCompanyList('Inactive',          engagement.inactive.companies,      TIERS.atRisk.color)} />
            </div>
          </SectionCard>
        </div>
      </div>

      {/* ── Invite Modal ── */}
      {showReferralModal && (
        <Modal onClose={() => setShowReferralModal(false)}>
          <div className="bg-slate-900 px-6 py-5 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
                  <Gift className="h-4.5 w-4.5 text-white h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white">Invite Organizations</h2>
                  <p className="text-xs text-slate-400">Share your coworking space link</p>
                </div>
              </div>
              <button onClick={() => setShowReferralModal(false)} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Link className="h-3.5 w-3.5" />
                Invitation Link
              </label>
              <div className="flex gap-2">
                <Input value={referralLink} readOnly className="flex-1 bg-slate-50 border-slate-200 text-xs font-mono h-9" />
                <Button
                  onClick={copyReferralLink}
                  size="sm"
                  className="bg-slate-900 hover:bg-slate-800 text-white h-9 px-4 gap-1.5 text-xs"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {referralCopied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <p className="text-xs text-slate-400">Organizations that register via this link will appear in your dashboard.</p>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Share2 className="h-3.5 w-3.5" />
                Share via
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Join our coworking space on Evolvv: ${referralLink}`)}`, '_blank')}
                  className="flex items-center justify-center gap-2 bg-green-50 hover:bg-green-100 border border-green-100 text-green-700 text-sm font-medium py-2.5 rounded-xl transition-colors"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#16a34a"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </button>
                <button
                  onClick={() => {
                    const subject = encodeURIComponent('Join Our Coworking Space on Evolvv');
                    const body = encodeURIComponent(`Hi,\n\nWe'd love to have your organization join our coworking space.\n\nSign up here: ${referralLink}\n\nBest regards`);
                    window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`, '_blank');
                  }}
                  className="flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 border border-red-100 text-red-700 text-sm font-medium py-2.5 rounded-xl transition-colors"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/></svg>
                  Email
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Company List Modal ── */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)} wide>
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{modalData.title}</h2>
              <p className="text-xs text-slate-400 mt-0.5">{modalData.companies.length} organization{modalData.companies.length !== 1 ? 's' : ''}</p>
            </div>
            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {modalData.companies.length > 0 ? (
              <div className="space-y-1.5">
                {modalData.companies.map((company, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors group">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${modalData.color}15` }}>
                        <Building2 className="h-4 w-4" style={{ color: modalData.color }} />
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        {company.name || company.companyName || `Organization ${i + 1}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{company.totalInvoices || 0} invoices</span>
                      <span className="text-sm font-semibold" style={{ color: modalData.color }}>{company.healthScore || 0}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <Building2 className="h-8 w-8 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No organizations in this category yet.</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

const StatCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
      <div className={`${iconBg} p-2 rounded-lg`}>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>
    </div>
    <div className="text-2xl font-bold text-slate-900 mb-0.5">{value}</div>
    <p className="text-xs text-slate-400">{sub}</p>
  </div>
);

const SectionCard = ({ title, icon, children, padded }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
    <div className={`px-6 py-4 flex items-center gap-2 border-b border-slate-50`}>
      {icon}
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
    </div>
    <div className={padded ? 'px-6 py-5' : 'py-2'}>{children}</div>
  </div>
);

const MetricBar = ({ label, count, percentage, color, onClick }) => (
  <div className="group cursor-pointer" onClick={onClick}>
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-2">
        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs text-slate-600">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-slate-900">{count}</span>
        <span className="text-xs text-slate-400 w-8 text-right">{Math.round(percentage)}%</span>
        <ChevronRight className="h-3 w-3 text-slate-300 group-hover:text-slate-500 transition-colors" />
      </div>
    </div>
    <div className="w-full bg-slate-100 rounded-full h-1.5 group-hover:bg-slate-200 transition-colors overflow-hidden">
      <div
        className="h-1.5 rounded-full transition-all duration-700"
        style={{ width: `${Math.min(percentage, 100)}%`, backgroundColor: color, opacity: 0.8 }}
      />
    </div>
  </div>
);

const PerformerItem = ({ rank, name, score, metric, color }) => (
  <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors rounded-xl">
    <span className="text-xs font-bold text-slate-300 w-4 text-center flex-shrink-0">{rank}</span>
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
      <Building2 className="h-3.5 w-3.5" style={{ color }} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-slate-800 truncate">{name}</div>
      <div className="text-xs text-slate-400">{metric}</div>
    </div>
    <span className="text-sm font-bold flex-shrink-0" style={{ color }}>{score}%</span>
  </div>
);

const EmptyNote = ({ text }) => (
  <div className="px-4 py-6 text-center">
    <p className="text-xs text-slate-400">{text}</p>
  </div>
);

const Modal = ({ children, onClose, wide }) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className={`bg-white rounded-2xl shadow-2xl w-full overflow-hidden ${wide ? 'max-w-lg' : 'max-w-md'}`}>
      {children}
    </div>
  </div>
);

export default AdminPanel;
